import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PublicHeader from "@/components/common/PublicHeader";

// Brand colors — kept in sync with LandingPage / PublicHeader.
const PRIMARY = "#1B5189";

interface LegalLayoutProps {
  title: string;
  /** Short one-line description shown under the title. */
  subtitle: string;
  /** Human-readable last-updated date, e.g. "11 Juni 2026". */
  lastUpdated: string;
  children: React.ReactNode;
}

/**
 * Shared chrome for public legal pages (Privacy Policy, Terms of Service).
 * Renders the public header, a brand title band, the document body in a
 * readable column, and a minimal footer that cross-links the legal pages.
 */
export default function LegalLayout({
  title,
  subtitle,
  lastUpdated,
  children,
}: LegalLayoutProps) {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <PublicHeader />

      {/* Title band */}
      <section
        className="px-6 py-14 text-white"
        style={{ backgroundColor: PRIMARY }}
      >
        <div className="mx-auto max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-blue-100 transition hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali ke Beranda
          </Link>
          <h1 className="mt-5 text-3xl font-bold sm:text-4xl">{title}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-blue-100 sm:text-base">
            {subtitle}
          </p>
          <p className="mt-4 text-xs text-blue-200">
            Terakhir diperbarui: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Body */}
      <main className="mx-auto max-w-3xl px-6 py-14">
        <div className="space-y-10 text-sm leading-relaxed text-slate-700">
          {children}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-200 pt-6 text-sm">
          <Link to="/privacy" className="font-medium hover:text-blue-600" style={{ color: PRIMARY }}>
            Kebijakan Privasi
          </Link>
          <Link to="/terms" className="font-medium hover:text-blue-600" style={{ color: PRIMARY }}>
            Syarat &amp; Ketentuan
          </Link>
          <a
            href="mailto:dja@kemenkeu.go.id"
            className="font-medium hover:text-blue-600"
            style={{ color: PRIMARY }}
          >
            Hubungi Kami
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-3xl px-6 py-8 text-xs text-slate-500">
          © 2026 Direktorat Jenderal Anggaran, Kementerian Keuangan Republik
          Indonesia. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

// ── Small content helpers ──────────────────────────────────────────────────────

/** A numbered/titled section within a legal document. */
export function LegalSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

/** A bulleted list with consistent styling. */
export function LegalList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 marker:text-slate-400">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}
